import { useEffect, useState } from 'react';
import { X, FileSpreadsheet, AlertTriangle, Printer, Mail } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { downloadBlob } from '../../../utils/downloadBlob';
import { openDocument } from '../../../utils/openDocument';
import { formatDateTimeInTz } from '../../../utils/timezone';
import { cn } from '../../../utils/cn';
import type { SupplierManifest, SupplierManifestHeader, SupplierManifestOrder } from '../../../types/nursery';

/**
 * The supplier manifest Bunnings' greenlife cross dock (Plant Access) wants
 * with the truck: one row per purchase order, with what it went on. The file
 * is Bunnings' own workbook with these filled in, so its store lookup,
 * checks and totals still work when they open it.
 *
 * Trees come from the picks once picking has started, else from the order,
 * and the row says which. The docket number is D and the order's invoice
 * number; an order not invoiced yet goes on with its order number, and says so. Trays, stillages and racks are known to nobody but
 * whoever loaded the truck, so they are typed here and kept on the load.
 *
 * Two stores' trees often share a stillage. The PO riding on another's picks
 * it under "Stillage"; it then has no stillage count, and the sheet names
 * whose stillage it is in its "Shared stillage with" column.
 *
 * The usual recipients are the organisation's, kept from one manifest to the
 * next; changing them here changes them for every load.
 */

export interface SupplierManifestDialogProps {
  loadId: number;
  timezone: string;
  onClose: () => void;
}

type Packing = { trays: string; stillages: string; seedling_racks: string; shares_with: string };
const EMPTY: Packing = { trays: '', stillages: '', seedling_racks: '', shares_with: '' };
type HeaderText = Record<keyof SupplierManifestHeader, string>;
type Action = 'save' | 'download' | 'print' | 'email';

const HEADER_FIELDS: { key: Exclude<keyof SupplierManifestHeader, 'total_stacks' | 'total_pallets' | 'phac_numbers'>; label: string; hint?: string; inputMode?: 'email' | 'tel' }[] = [
  { key: 'supplier_code', label: 'Bunnings supplier code', hint: 'Three letters and three numbers' },
  { key: 'batch_number', label: 'Batch #' },
  { key: 'contact_name', label: 'Contact name' },
  { key: 'contact_phone', label: 'Contact number', inputMode: 'tel' },
  { key: 'contact_email', label: 'Contact email', inputMode: 'email' },
];

const PACKING: { key: Exclude<keyof Packing, 'shares_with'>; label: string }[] = [
  { key: 'trays', label: 'Trays' },
  { key: 'stillages', label: 'Stillages' },
  { key: 'seedling_racks', label: 'Seedling racks' },
];

const asText = (n: number | null | undefined) => (n == null ? '' : String(n));
const asCount = (s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : NaN;
};
const storeOf = (o: SupplierManifestOrder) => o.store_name || o.customer_name || o.order_number;
const parseEmails = (text: string) => [...new Set(text.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

export function SupplierManifestDialog({ loadId, timezone, onClose }: SupplierManifestDialogProps) {
  const [manifest, setManifest] = useState<SupplierManifest | null>(null);
  const [header, setHeader] = useState<HeaderText | null>(null);
  const [packing, setPacking] = useState<Record<number, Packing>>({});
  const [recipients, setRecipients] = useState('');
  const [busy, setBusy] = useState<Action | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const take = (m: SupplierManifest) => {
    setManifest(m);
    setHeader({
      supplier_code: m.header.supplier_code ?? '',
      batch_number: m.header.batch_number ?? '',
      phac_numbers: m.header.phac_numbers ?? '',
      contact_name: m.header.contact_name ?? '',
      contact_phone: m.header.contact_phone ?? '',
      contact_email: m.header.contact_email ?? '',
      total_stacks: asText(m.header.total_stacks),
      total_pallets: asText(m.header.total_pallets),
    });
    setPacking(Object.fromEntries(m.orders.map((o) => [o.order_id, {
      trays: asText(o.trays), stillages: asText(o.stillages), seedling_racks: asText(o.seedling_racks),
      shares_with: o.shares_with_order_id ? String(o.shares_with_order_id) : '',
    }])));
    setRecipients((m.recipients ?? []).join(', '));
  };

  useEffect(() => {
    nurseryService.getLoadManifest(loadId)
      .then((res) => (res.success ? take(res.data) : setError(errorMessage(res, 'Could not open the manifest'))))
      .catch((e) => setError(errorMessage(e, 'Could not open the manifest')));
  }, [loadId]);

  const orders = manifest?.orders ?? [];
  const counts = orders.map((o) => {
    const p = packing[o.order_id] ?? EMPTY;
    const sharesWith = p.shares_with ? Number(p.shares_with) : null;
    return {
      order_id: o.order_id,
      trays: asCount(p.trays),
      // On another's stillage: counted on that row, not this one.
      stillages: sharesWith ? null : asCount(p.stillages),
      seedling_racks: asCount(p.seedling_racks),
      shares_with_order_id: sharesWith,
    };
  });
  // Only an order with its own stillage can carry another's trees.
  const hosts = (o: SupplierManifestOrder) => orders.filter((h) => h.order_id !== o.order_id && !packing[h.order_id]?.shares_with);
  const riders = (o: SupplierManifestOrder) => orders.filter((r) => packing[r.order_id]?.shares_with === String(o.order_id));
  const totals = header ? { total_stacks: asCount(header.total_stacks), total_pallets: asCount(header.total_pallets) } : null;
  const invalid = counts.some((c) => [c.trays, c.stillages, c.seedling_racks].some((n) => Number.isNaN(n)))
    || (totals !== null && (Number.isNaN(totals.total_stacks) || Number.isNaN(totals.total_pallets)));
  const emails = parseEmails(recipients);
  const badEmails = emails.filter((e) => !EMAIL.test(e));
  const noPo = orders.filter((o) => !o.customer_po);
  const noInvoice = orders.filter((o) => !o.docket_number);
  const notPicked = orders.some((o) => o.items_from === 'ordered');
  const sum = (key: 'items' | 'trays' | 'stillages' | 'seedling_racks') => (key === 'items'
    ? orders.reduce((n, o) => n + o.items, 0)
    : counts.reduce((n, c) => n + (Number(c[key]) || 0), 0));
  const blocked = busy !== null || !manifest || invalid || badEmails.length > 0;

  const save = async (): Promise<boolean> => {
    if (!header || !totals) return false;
    const res = await nurseryService.saveLoadManifest(loadId, {
      header: {
        supplier_code: header.supplier_code.trim() || null,
        batch_number: header.batch_number.trim() || null,
        phac_numbers: header.phac_numbers.trim() || null,
        contact_name: header.contact_name.trim() || null,
        contact_phone: header.contact_phone.trim() || null,
        contact_email: header.contact_email.trim() || null,
        total_stacks: totals.total_stacks,
        total_pallets: totals.total_pallets,
      },
      orders: counts,
      recipients: emails,
    });
    if (!res.success) { setError(errorMessage(res, 'Could not save the manifest')); return false; }
    take(res.data);
    return true;
  };

  const name = `supplier-manifest-${manifest?.load.load_number ?? loadId}`;
  const run = async (what: Action) => {
    setBusy(what);
    setError(null);
    setDone(null);
    try {
      // Whatever was typed goes into the file, so save first, always.
      if (!(await save())) return;
      if (what === 'download') {
        downloadBlob(await nurseryService.getLoadManifestFile(loadId), `${name}.xlsx`);
      } else if (what === 'print') {
        await openDocument(`/nursery/loads/${loadId}/manifest.pdf`, `${name}.pdf`);
      } else if (what === 'email') {
        const res = await nurseryService.emailLoadManifest(loadId, emails);
        if (!res.success) { setError(errorMessage(res, 'The manifest was not emailed')); return; }
        take(res.data);
        setDone(`Emailed to ${emails.join(', ')}.`);
      } else {
        setDone('Saved.');
      }
    } catch (e) {
      setError(errorMessage(e, what === 'save' ? 'Could not save the manifest' : what === 'email' ? 'The manifest was not emailed' : 'Could not make the manifest'));
      // A failed send is logged on the load; show it in the list.
      if (what === 'email') {
        nurseryService.getLoadManifest(loadId).then((res) => { if (res.success) setManifest(res.data); }).catch(() => undefined);
      }
    } finally {
      setBusy(null);
    }
  };

  const setHead = (key: keyof HeaderText, value: string) => setHeader((h) => (h ? { ...h, [key]: value } : h));
  const setPack = (orderId: number, key: keyof Packing, value: string) =>
    setPacking((p) => ({ ...p, [orderId]: { ...(p[orderId] ?? EMPTY), [key]: value } }));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="manifest-title">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div className="min-w-0">
            <h2 id="manifest-title" className="text-lg font-semibold text-neutral-900">Supplier manifest</h2>
            <p className="text-sm text-neutral-500">
              Bunnings&apos; booking sheet for the cross dock, one row per purchase order, as their own spreadsheet.
              Print a copy for the truck and email it from here.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-neutral-100 flex-shrink-0">
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
          {done && <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800 break-words">{done}</div>}

          {!manifest || !header ? (
            !error && <div className="h-40 bg-neutral-100 rounded-lg animate-pulse" />
          ) : (
            <>
              <section aria-labelledby="manifest-supplier" className="space-y-3">
                <h3 id="manifest-supplier" className="text-sm font-semibold text-neutral-700">Supplier</h3>
                <p className="text-sm text-neutral-700 break-words">
                  <span className="font-medium">{manifest.supplier.name}</span>
                  {manifest.supplier.address ? <span className="text-neutral-500"> · {manifest.supplier.address}</span> : null}
                </p>
                {!manifest.saved && (
                  <p className="text-xs text-neutral-500">Filled in from the last manifest you saved. Check the batch before sending.</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {HEADER_FIELDS.map((f) => (
                    <label key={f.key} className="block text-sm">
                      <span className="text-neutral-700">{f.label}</span>
                      <input type="text" inputMode={f.inputMode} className={`${inputClass} mt-1`}
                        value={header[f.key]} onChange={(e) => setHead(f.key, e.target.value)} />
                      {f.hint && <span className="text-xs text-neutral-500">{f.hint}</span>}
                    </label>
                  ))}
                  <label className="block text-sm sm:col-span-2">
                    <span className="text-neutral-700">PHAC numbers, if required</span>
                    <input type="text" className={`${inputClass} mt-1`}
                      value={header.phac_numbers} onChange={(e) => setHead('phac_numbers', e.target.value)} />
                  </label>
                </div>
              </section>

              <section aria-labelledby="manifest-orders" className="space-y-2">
                <h3 id="manifest-orders" className="text-sm font-semibold text-neutral-700">
                  Purchase orders <span className="font-normal text-neutral-500">({orders.length})</span>
                </h3>
                <p className="text-xs text-neutral-500">
                  Two stores on one stillage: on the second, choose whose stillage it is under Stillage. The sheet counts the
                  stillage once and names the store it is shared with.
                  {notPicked && ' Where picking has not started, trees are what was ordered; download again once it is picked.'}
                </p>
                {noPo.length > 0 && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span className="min-w-0 break-words">
                      {noPo.map((o) => o.order_number).join(', ')} {noPo.length === 1 ? 'has' : 'have'} no purchase order number.
                      The sheet works out the store from it, so add it on the order first.
                    </span>
                  </div>
                )}
                {noInvoice.length > 0 && (
                  <p className="text-xs text-neutral-500 break-words">
                    The docket number is D and the invoice number. {noInvoice.length === orders.length ? 'None of these is' : `${noInvoice.length} of these ${noInvoice.length === 1 ? 'is' : 'are'}`} invoiced
                    yet, so the order number is used until it is: an order is invoiced when its picking is finished.
                  </p>
                )}
                <ul className="space-y-2">
                  {orders.map((o) => {
                    const p = packing[o.order_id] ?? EMPTY;
                    const carrying = riders(o);
                    return (
                      <li key={o.order_id} className="border border-neutral-200 rounded-lg p-3">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
                          <span className="font-medium text-neutral-900 break-words min-w-0">{o.customer_name ?? 'No customer'}</span>
                          {o.customer_code && <span className="text-neutral-500">#{o.customer_code}</span>}
                          <span className="ml-auto text-neutral-700 whitespace-nowrap">
                            {o.items} {o.items === 1 ? 'tree' : 'trees'}
                            <span className="text-neutral-500"> {o.items_from === 'picked' ? 'picked' : 'ordered'}</span>
                          </span>
                        </div>
                        <p className="text-xs text-neutral-500 break-words">
                          PO {o.customer_po ?? '—'} · Docket {o.docket_number ?? `${o.order_number} (not invoiced yet)`}
                          {o.items_from === 'picked' && o.picked_trees < o.ordered_trees ? ` · ${o.ordered_trees - o.picked_trees} short of the order` : ''}
                        </p>
                        <label className="block text-xs text-neutral-600 mt-2">
                          Stillage
                          <select className={`${inputClass} mt-0.5`} aria-label={`Stillage for ${o.order_number}`}
                            value={p.shares_with} disabled={carrying.length > 0}
                            onChange={(e) => setPack(o.order_id, 'shares_with', e.target.value)}>
                            <option value="">Its own</option>
                            {hosts(o).map((h) => (
                              <option key={h.order_id} value={String(h.order_id)}>
                                {`Shared with ${storeOf(h)}${h.customer_po ? ` (PO ${h.customer_po})` : ''}`}
                              </option>
                            ))}
                          </select>
                        </label>
                        {carrying.length > 0 && (
                          <p className="text-xs text-neutral-500 mt-1 break-words">
                            Also carrying {carrying.map(storeOf).join(', ')}.
                          </p>
                        )}
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          {PACKING.map((f) => (f.key === 'stillages' && p.shares_with ? (
                            <p key={f.key} className="text-xs text-neutral-500 self-end pb-2">Counted on the shared row</p>
                          ) : (
                            <label key={f.key} className="block text-xs text-neutral-600">
                              {f.label}
                              <input type="text" inputMode="numeric" className={`${inputClass} mt-0.5 text-right`}
                                aria-label={`${f.label} for ${o.order_number}`}
                                value={p[f.key]} onChange={(e) => setPack(o.order_id, f.key, e.target.value)} />
                            </label>
                          )))}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="text-sm text-neutral-700">
                  Total: {sum('items')} trees · {sum('trays')} trays · {sum('stillages')} stillages · {sum('seedling_racks')} seedling racks
                </p>
              </section>

              <section className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-neutral-700">Total stacks</span>
                  <input type="text" inputMode="numeric" className={`${inputClass} mt-1`}
                    value={header.total_stacks} onChange={(e) => setHead('total_stacks', e.target.value)} />
                </label>
                <label className="block text-sm">
                  <span className="text-neutral-700">Total pallets</span>
                  <input type="text" inputMode="numeric" className={`${inputClass} mt-1`}
                    value={header.total_pallets} onChange={(e) => setHead('total_pallets', e.target.value)} />
                </label>
              </section>
              {invalid && <p className="text-sm text-red-700">Counts are whole numbers, or left blank.</p>}

              <section aria-labelledby="manifest-email" className="space-y-2">
                <h3 id="manifest-email" className="text-sm font-semibold text-neutral-700">Email to</h3>
                <textarea rows={2} className={inputClass} aria-labelledby="manifest-email"
                  placeholder="bookings@plantaccess.com.au, office@…"
                  value={recipients} onChange={(e) => setRecipients(e.target.value)} />
                <p className="text-xs text-neutral-500">
                  Your usual list for every manifest: changing it here changes it for the next load too.
                  Replies go to the contact email above.
                </p>
                {badEmails.length > 0 && (
                  <p className="text-sm text-red-700 break-words">Not an email address: {badEmails.join(', ')}</p>
                )}
                {(manifest.emails ?? []).length > 0 && (
                  <ul className="text-xs text-neutral-600 space-y-0.5">
                    {(manifest.emails ?? []).map((e) => (
                      <li key={e.sent_at} className={cn('break-words', !e.sent_ok && 'text-red-700')}>
                        {e.sent_ok ? 'Emailed' : 'Not sent'} {formatDateTimeInTz(e.sent_at, timezone)} to {e.recipients.join(', ')}
                        {e.sent_by ? ` by ${e.sent_by}` : ''}{!e.sent_ok && e.error ? `: ${e.error}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-5 pb-5">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy !== null}>Close</Button>
          <Button variant="outline" size="sm" onClick={() => run('save')} disabled={blocked}>
            {busy === 'save' ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => run('print')} disabled={blocked || orders.length === 0}>
            <Printer className="h-4 w-4" /> {busy === 'print' ? 'Making…' : 'Print'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => run('download')} disabled={blocked || orders.length === 0}>
            <FileSpreadsheet className="h-4 w-4" /> {busy === 'download' ? 'Making…' : 'Download'}
          </Button>
          <Button size="sm" onClick={() => run('email')} disabled={blocked || orders.length === 0 || emails.length === 0}>
            <Mail className="h-4 w-4" /> {busy === 'email' ? 'Sending…' : emails.length ? `Email ${emails.length === 1 ? '1 person' : `${emails.length} people`}` : 'Email'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SupplierManifestDialog;
